<div class="mx-auto max-w-(--breakpoint-2xl)">
  <app-data-table
    (route)="this.handleRoute($event)"
    [title]="'Products'"
    [dataPage]="productsPage"
    [columns]="tableColumns"
    [rowActions]="rowActions"
    [rowRouterLinkTemplate]="isAdmin ? '/products/:id/edit' : undefined"
    [loading]="isLoading"
  >
      <div header class="flex w-full flex-row items-center justify-between gap-3">
        <div class="flex w-full justify-start gap-3 items-end">
          <app-text-input [control]="searchControl" [placeholder]="'Search products'" [clearable]="true"></app-text-input>
          <div class="w-60">
            <app-select
              [height]="'11'"
              [multiple]="true"
              [control]="categoriesControl"
              [choices]="categories"
              [placeholder]="'Filter by categories'"
              [label]="''"></app-select>
          </div>
        </div>
        <div class="flex w-full justify-end">
          @if (isAdmin) {
            <a routerLink="/products/create"
               class="h-12 text-theme-sm shadow-theme-xs inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2.5 font-medium
            text-gray-700 hover:bg-gray-50 hover:text-gray-800 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-white/[0.03] dark:hover:text-gray-200">
              <lucide-icon [name]="PlusIcon"></lucide-icon>
              Add Product
            </a>
          }
        </div>
      </div>
  </app-data-table>
</div>

<app-modal
  [title]="'Delete Product'"
  [content]="'Are you sure you want to delete product ' + '&quot;' + (productToDelete?.name || '') + '&quot;' + '?'"
  [show]="!!productToDelete"
  [closeButtonLabel]="'Cancel'"
  [confirmButtonLabel]="'Yes, Proceed'"
  (onClose)="productToDelete = undefined"
  (onConfirm)="deleteProductConfirmed()"
></app-modal>

<app-modal
  [title]="'Duplicate Product'"
  [show]="!!productToDuplicate"
  [closeButtonLabel]="'Cancel'"
  [confirmButtonLabel]="'Create Copy'"
  [processing]="duplicateProcessing"
  (onClose)="cancelDuplicate()"
  (onConfirm)="confirmDuplicate()"
>
  <div class="space-y-3 text-left">
    <p class="text-sm text-gray-600 dark:text-gray-400">You are about to duplicate:</p>
    <p class="text-base font-semibold text-gray-900 dark:text-white break-words">
      “{{ productToDuplicate?.name || 'Unnamed product' }}”
    </p>
    <p class="text-sm text-gray-600 dark:text-gray-400">
      The new product will be saved as:
      <span class="font-medium text-gray-800 dark:text-gray-200 break-words">“{{ duplicateNamePreview() }}”</span>
    </p>
    @if (duplicateProcessing) {
      <div class="rounded-lg border border-blue-100 bg-blue-50 px-3 py-2 text-sm text-blue-700 dark:border-blue-900/60 dark:bg-blue-900/20 dark:text-blue-200">
        <div class="font-medium">Status: {{ duplicateStatusLabel() }}</div>
        <div>This can take a minute for large products. Please keep this window open.</div>
      </div>
    }
  </div>
</app-modal>

@if (productUsageDetails) {
  <app-modal
    [title]="productUsageContext === 'delete' ? 'Cannot Delete Product' : 'Product Usage'"
    [show]="!!productUsageDetails"
    [closeButtonLabel]="'Close'"
    [confirm]="false"
    (onClose)="closeProductUsageModal()"
  >
    <div class="space-y-4">
      @if (productUsageDetails.event_count > 0) {
        <p class="text-sm text-gray-700 dark:text-gray-300">
          @if (productUsageContext === 'delete') {
            This product cannot be deleted because it is currently being used in <strong>{{ productUsageDetails.event_count }}</strong> event(s):
          } @else {
            {{ productUsageProduct?.name || 'This product' }} is currently used in <strong>{{ productUsageDetails.event_count }}</strong> event(s):
          }
        </p>

        <div class="max-h-60 overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-700">
          <table class="min-w-full text-sm">
            <thead class="bg-gray-50 dark:bg-gray-800/40">
              <tr>
                <th class="px-4 py-2 text-left font-medium text-gray-600 dark:text-gray-300">Event</th>
                <th class="px-4 py-2 text-left font-medium text-gray-600 dark:text-gray-300">Date</th>
                <th class="px-4 py-2 text-right font-medium text-gray-600 dark:text-gray-300">Open</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-200 dark:divide-gray-700">
              @for (event of productUsageDetails.events; track event.id) {
                <tr class="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                  <td class="px-4 py-3">
                    <a [href]="'/events/' + event.id"
                       target="_blank"
                       class="font-medium text-gray-900 hover:text-brand-600 dark:text-white dark:hover:text-brand-400">
                      {{ event.name }}
                    </a>
                    <div class="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      @if (event.reference) {
                        Ref: {{ event.reference }}
                      } @else {
                        ID: {{ event.id }}
                      }
                    </div>
                  </td>
                  <td class="px-4 py-3 text-gray-500 dark:text-gray-400">
                    {{ event.date || '—' }}
                  </td>
                  <td class="px-4 py-3 text-right">
                    <a [href]="'/events/' + event.id"
                       target="_blank"
                       class="inline-flex items-center gap-1 text-gray-500 hover:text-brand-600 dark:text-gray-400 dark:hover:text-brand-400">
                      View
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"></path>
                      </svg>
                    </a>
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>

        @if (productUsageContext === 'delete') {
          <p class="text-sm text-gray-600 dark:text-gray-400">
            To delete this product, please remove it from these events first.
          </p>
        }
      } @else {
        <p class="text-sm text-gray-600 dark:text-gray-400">
          {{ productUsageProduct?.name || 'This product' }} is not used in any events yet.
        </p>
      }
    </div>
  </app-modal>
}

@if (productDeleteError) {
  <app-modal
    [title]="'Error'"
    [content]="productDeleteError"
    [show]="!!productDeleteError"
    [closeButtonLabel]="'Close'"
    (onClose)="productDeleteError = undefined"
    [confirm]="false"
  ></app-modal>
}
